import type { CaseAward } from '@lnr-v2/types';

export interface AwardCatalogItem {
  key: string;
  translationKey: string;
  fallbackLabel: {
    en: string;
    fr: string;
  };
  defaultImageUrl?: string;
}

export interface ResolvedCaseAward extends CaseAward {
  displayLabel: string;
  translationKey: string;
  resolvedImageUrl?: string;
}

const normalizeAwardKey = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export const CASE_AWARDS_CATALOG: Record<string, AwardCatalogItem> = {
  fwa: {
    key: 'fwa',
    translationKey: 'cases.awards.catalog.fwa',
    fallbackLabel: { en: 'FWA', fr: 'FWA' },
    defaultImageUrl: '/assets/prices/FWA.png',
  },
  awwwards: {
    key: 'awwwards',
    translationKey: 'cases.awards.catalog.awwwards',
    fallbackLabel: { en: 'Awwwards', fr: 'Awwwards' },
    defaultImageUrl: '/assets/prices/Awwwwards.png',
  },
  cssdesignawards: {
    key: 'cssda',
    translationKey: 'cases.awards.catalog.cssda',
    fallbackLabel: { en: 'CSS Design Awards', fr: 'CSS Design Awards' },
    defaultImageUrl: '/assets/prices/CSSDesignAward.png',
  },
  contentsquarer: {
    key: 'contentsquarer',
    translationKey: 'cases.awards.catalog.contentsquarer',
    fallbackLabel: { en: 'Content squarer', fr: 'Content squarer' },
    defaultImageUrl: '/assets/prices/content_square.png',
  },
  webflow: {
    key: 'webflow',
    translationKey: 'cases.awards.catalog.webflow',
    fallbackLabel: { en: 'Webflow', fr: 'Webflow' },
    defaultImageUrl: '/assets/prices/Webflow.png',
  },
  codrops: {
    key: 'codrops',
    translationKey: 'cases.awards.catalog.codrops',
    fallbackLabel: { en: 'Codrops', fr: 'Codrops' },
    defaultImageUrl: '/assets/prices/codrops.png',
  },
  associationnationaledescommunicants: {
    key: 'associationnationaledescommunicants',
    translationKey: 'cases.awards.catalog.associationnationaledescommunicants',
    fallbackLabel: {
      en: 'Association nationale des communicants',
      fr: 'Association nationale des communicants',
    },
    defaultImageUrl:
      '/assets/prices/Association_nationale_des%20communicants.png',
  },
  m: {
    key: 'm',
    translationKey: 'cases.awards.catalog.m',
    fallbackLabel: { en: 'M', fr: 'M' },
    defaultImageUrl: '/assets/prices/muzli.png',
  },
  frenchdesign: {
    key: 'frenchdesign',
    translationKey: 'cases.awards.catalog.frenchdesign',
    fallbackLabel: { en: 'FrenchDesign', fr: 'FrenchDesign' },
    defaultImageUrl: '/assets/prices/french_design.png',
  },
};

const sortByOrderAndYear = (a: CaseAward, b: CaseAward) => {
  const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;

  if (orderA !== orderB) {
    return orderA - orderB;
  }

  const yearA = a.year ?? 0;
  const yearB = b.year ?? 0;
  return yearB - yearA;
};

export const resolveCaseAwards = (
  awards: CaseAward[] | undefined,
  language: 'en' | 'fr',
  t: (key: string) => string
): ResolvedCaseAward[] => {
  if (!awards || awards.length === 0) {
    return [];
  }

  return [...awards].sort(sortByOrderAndYear).map((award) => {
    const normalizedKey = normalizeAwardKey(award.key);
    const catalog = CASE_AWARDS_CATALOG[normalizedKey];
    const translationKey =
      catalog?.translationKey || `cases.awards.catalog.${normalizedKey}`;
    const translated = t(translationKey);
    const fallback =
      award.label ||
      catalog?.fallbackLabel[language] ||
      catalog?.fallbackLabel.en ||
      award.key;

    return {
      ...award,
      translationKey,
      displayLabel: translated !== translationKey ? translated : fallback,
      resolvedImageUrl: award.imageUrl || catalog?.defaultImageUrl,
    };
  });
};
